/* PGEmergencyContact.js
   PG-specific Emergency Contact component.
   Replaces the combined Parents + Guardian sections for the PG flow.
   Fields per SOW Step 7:
     - Relationship (dropdown)
     - Given Name, Middle Name (optional), Family Name
     - Mobile Number, Email Address
   Removed per SOW: Profession, Company, Company Address, Address
   Zero modifications to existing GuardianInformation.js or ParentsInformation.js
*/
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import InputRow from './InputRow'
import Loading from './Loading'
import { emailCheck } from '../constants'

// Relationship options fetched from API (CODE_RELATIONSHIP table)
// Returns {Id: RelationTypeId, value: LONG_DESC}

export default class PGEmergencyContact extends Component {
  constructor (props) {
    super(props)
    const info = props.emergencyContactInfo || {}
    this.state = {
      /* Accept both capitalised (backend XML) and lowercase (Main.js state) keys */
      relationship: info.relationship || info.Relationship || '',
      givenName: info.givenName || info.GivenName || '',
      middleName: info.middleName || info.MiddleName || '',
      familyName: info.familyName || info.FamilyName || '',
      mobileNumber: info.mobileNumber || info.MobileNumber || '',
      emailAddress: info.emailAddress || info.EmailAddress || '',
      loading: false,
      relationshipData: [],
      mobileError: ''
    }
    this.publish = this.publish.bind(this)
  }

  componentDidMount () {
    fetch(`${process.env.REACT_APP_API_URL}/api/GuardianInformation/GetRelations`)
      .then(res => res.json())
      .then(res => {
        this.setState({ relationshipData: res || [] })
      })
      .catch(() => {})

    this.publish()
  }

  publish (override = {}) {
    const s = { ...this.state, ...override }
    this.props.collectState('PG_EC', {
      relationship: s.relationship,
      givenName: s.givenName,
      middleName: s.middleName,
      familyName: s.familyName,
      mobileNumber: s.mobileNumber,
      emailAddress: s.emailAddress
    })
  }

  render () {
    const { loading, relationship, givenName, middleName, familyName, mobileNumber, emailAddress, relationshipData } = this.state
    const { error } = this.props

    if (loading) return <Loading />

    return (
      <div>
        {/* Relationship — dropdown as per SOW */}
        <InputRow
          rowName='Relationship'
          name='select'
          value={relationship}
          data={relationshipData}
          required='*'
          errMsg={error.relationship || ''}
          onChange={(val) => {
            this.setState({ relationship: val }, () => this.publish())
          }}
        />

        {/* Given Name */}
        <InputRow
          rowName='Given Name'
          name='text'
          value={givenName}
          required='*'
          errMsg={error.givenName || ''}
          onChange={(val) => {
            this.setState({ givenName: val }, () => this.publish())
          }}
        />

        {/* Middle Name */}
        <InputRow
          rowName='Middle Name'
          name='text'
          value={middleName}
          required='*'
          errMsg={error.middleName || ''}
          onChange={(val) => {
            this.setState({ middleName: val }, () => this.publish())
          }}
        />

        {/* Family Name */}
        <InputRow
          rowName='Family Name'
          name='text'
          value={familyName}
          required='*'
          errMsg={error.familyName || ''}
          onChange={(val) => {
            this.setState({ familyName: val }, () => this.publish())
          }}
        />

        {/* Mobile Number */}
        <InputRow
          rowName='Mobile Number'
          name='text'
          value={mobileNumber}
          required='*'
          errMsg={error.mobileNumber || ''}
          onChange={(val) => {
            if (/^\d*$/.test(val) || val === '') {
              this.setState({ mobileNumber: val, mobileError: '' }, () => this.publish())
            } else {
              this.setState({ mobileError: 'Please enter numbers only.' })
            }
          }}
        />
        {this.state.mobileError && <p style={{ color: 'red', fontSize: 12, marginLeft: 5 }}>{this.state.mobileError}</p>}

        {/* Email Address */}
        <InputRow
          rowName='Email Address'
          name='text'
          value={emailAddress}
          required='*'
          errMsg={error.emailAddress || ''}
          onChange={(val) => {
            this.setState({ emailAddress: val }, () => this.publish())
          }}
        />

        <button className='save' onClick={() => { this.props.onSave() }}>Next</button>
      </div>
    )
  }
}

PGEmergencyContact.validate = function (data) {
  /* Static validation helper — used by Main.js conditionCheck */
  const errors = {}
  const noVal = [0, '', null, undefined, NaN, '0']
  if (noVal.includes(data.relationship)) errors.relationship = 'Please select a relationship'
  if (!data.givenName) errors.givenName = 'Please enter given name'
  if (!data.middleName) errors.middleName = 'Please enter middle name'
  if (!data.familyName) errors.familyName = 'Please enter family name'
  if (!data.mobileNumber) errors.mobileNumber = 'Please enter mobile number'
  if (!data.emailAddress) {
    errors.emailAddress = 'Please enter email address'
  } else if (!emailCheck.test(data.emailAddress)) {
    errors.emailAddress = 'Please enter a valid email address'
  }
  return errors
}

PGEmergencyContact.propTypes = {
  onSave: PropTypes.func.isRequired,
  collectState: PropTypes.func.isRequired,
  error: PropTypes.object,
  emergencyContactInfo: PropTypes.object,
  incompleteApplicationId: PropTypes.number
}

PGEmergencyContact.defaultProps = {
  error: {},
  emergencyContactInfo: {}
}
